import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { submissionsManagementService } from '../../../services/submissionsManagementService';
import { formatSubmissionFieldValue } from '../../../utils/submissionFieldFormatter';

const SubmissionDetailModal = ({ submission, labels, onClose, onLabelChange }) => {
  const [isAddingLabel, setIsAddingLabel] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const assignedLabelIds = submission?.submissionLabelAssignments?.map(a => a?.labelId) || [];
  const availableLabels = labels?.filter(l => !assignedLabelIds?.includes(l?.id));

  const handleAddLabel = async (labelId) => {
    setIsAddingLabel(true);
    try {
      await submissionsManagementService?.assignLabel(submission?.id, labelId);
      onLabelChange();
    } catch (err) {
      console.error('Error adding label:', err);
    } finally {
      setIsAddingLabel(false);
    }
  };

  const handleRemoveLabel = async (labelId) => {
    try {
      await submissionsManagementService?.removeLabel(submission?.id, labelId);
      onLabelChange();
    } catch (err) {
      console.error('Error removing label:', err);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date?.toLocaleString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // ── File-upload values ──────────────────────────────────────────────────────
  const isFileMeta = (v) => v && typeof v === 'object' && v._fileUpload === true;
  const asFileList = (v) => {
    if (isFileMeta(v)) return [v];
    if (Array.isArray(v) && v.length > 0 && v.every(isFileMeta)) return v;
    return null;
  };

  const handleDownloadFile = async (file) => {
    if (!file?.path) return;
    setDownloadError(null);
    try {
      const url = await submissionsManagementService?.getFileDownloadUrl(file.path);
      window.open(url, '_blank', 'noopener');
    } catch (err) {
      console.error('Error creating download link:', err);
      setDownloadError('Could not create a download link for this file. Please try again.');
    }
  };

  const renderFileValue = (files) => (
    <div className="space-y-2">
      {files?.map((f, i) => (
        <div key={i} className="flex items-center gap-2">
          <Icon name="Paperclip" size={14} className="text-muted-foreground flex-shrink-0" />
          <span className="text-sm text-foreground break-all">{f?.name}</span>
          <span className="text-xs text-muted-foreground flex-shrink-0">
            {f?.size != null ? `${(f.size / 1024).toFixed(1)} KB` : ''}
          </span>
          {f?.path ? (
            <Button
              variant="outline"
              size="xs"
              iconName="Download"
              onClick={() => handleDownloadFile(f)}
            >
              Download
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground italic">file not stored</span>
          )}
        </div>
      ))}
    </div>
  );

  const formatFieldValue = (key, value) => {
    const formElements = submission?.forms?.formElements || [];
    return formatSubmissionFieldValue(key, value, formElements, { jsonSpacing: 2 });
  };

  const renderSubmissionData = () => {
    const data = submission?.submissionData || {};
    
    if (Object.keys(data)?.length === 0) {
      return (
        <div className="text-center py-8">
          <Icon name="FileQuestion" size={48} className="text-muted-foreground mx-auto mb-3" />
          <p className="text-muted-foreground">No submission data available</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {downloadError && (
          <p className="text-sm text-red-500">{downloadError}</p>
        )}
        {Object.entries(data)?.map(([key, value]) => {
          const files = asFileList(value);
          return (
            <div key={key} className="border-b border-border pb-4 last:border-0">
              <label className="block text-sm font-medium text-muted-foreground mb-1 capitalize">
                {key?.replace(/_/g, ' ')}
              </label>
              {files ? renderFileValue(files) : (
                <p className="text-sm text-foreground">
                  {formatFieldValue(key, value)}
                </p>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card border border-border rounded-lg max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h2 className="text-xl font-semibold text-foreground mb-1">
                {submission?.forms?.title || 'Submission Details'}
              </h2>
              <p className="text-sm text-muted-foreground">
                Submitted on {formatDate(submission?.createdAt)}
              </p>
            </div>
            <Button
              variant="ghost"
              size="icon"
              iconName="X"
              onClick={onClose}
            />
          </div>

          {/* Meta Info */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Status</p>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium capitalize bg-primary/10 text-primary">
                {submission?.status || 'new'}
              </span>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Source</p>
              <p className="text-sm text-foreground">{submission?.source || 'Direct'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Form ID</p>
              <p className="text-sm text-foreground font-mono text-xs">
                {submission?.formId?.slice(0, 8)}...
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Submission ID</p>
              <p className="text-sm text-foreground font-mono text-xs">
                {submission?.id?.slice(0, 8)}...
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto flex-1 space-y-6">
          {/* Labels Section */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Tag" size={16} />
              Labels
            </h3>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {submission?.submissionLabelAssignments?.map((assignment) => (
                  <div
                    key={assignment?.labelId}
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-medium"
                    style={{
                      backgroundColor: `${assignment?.submissionLabels?.color}20`,
                      color: assignment?.submissionLabels?.color
                    }}
                  >
                    {assignment?.submissionLabels?.name}
                    <button
                      onClick={() => handleRemoveLabel(assignment?.labelId)}
                      className="hover:opacity-70 transition-opacity"
                    >
                      <Icon name="X" size={14} />
                    </button>
                  </div>
                ))}
                {submission?.submissionLabelAssignments?.length === 0 && (
                  <p className="text-sm text-muted-foreground">No labels assigned</p>
                )}
              </div>

              {availableLabels?.length > 0 && (
                <div className="flex items-center gap-2">
                  <div className="flex-1 max-w-xs">
                    <Select
                      options={availableLabels?.map(l => ({ value: l?.id, label: l?.name }))}
                      placeholder="Add label"
                      onChange={handleAddLabel}
                      loading={isAddingLabel}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Submission Data */}
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="FileText" size={16} />
              Submission Data
            </h3>
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              {renderSubmissionData()}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-border flex justify-end gap-3">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button
            variant="default"
            iconName="ExternalLink"
            iconPosition="right"
            onClick={() => window.open(`/analytics?tab=forms&id=${submission?.formId}`, '_blank')}
          >
            View Analytics
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubmissionDetailModal;